/* Change the surname on a garment already in the cart.

   The class can be corrected from the cart (snippets/klasse-field.liquid) and
   the name could not: a student who spelled it wrong, or left it blank and
   thought better of it, had to remove the garment and add it again. This is the
   name's half of that.

   /cart/change.js takes properties alongside the line key, and REPLACES the
   line's properties with whatever it is given - so the whole set is sent back,
   read from the data-properties attribute the snippet renders, with this one
   name overwritten. Sending an empty value drops the property, which is how a
   name is taken off again.

   Changing the properties changes the line key, so nothing can be patched in
   place afterwards. The page reloads instead, which is exactly what Impact's
   own quantity control does on the cart page (see _changeLineItemQuantity in
   assets/theme.js). */
(function () {
  "use strict";

  class LineItemProperty extends HTMLElement {
    connectedCallback() {
      this.text = this.querySelector("[data-surname-text]");
      this.editButton = this.querySelector("[data-surname-edit]");
      this.form = this.querySelector("[data-surname-form]");
      this.input = this.querySelector("[data-surname-input]");
      this.saveButton = this.querySelector("[data-surname-save]");
      if (!this.editButton || !this.form || !this.input || !this.saveButton) return;

      this.editButton.addEventListener("click", () => this.open());
      this.saveButton.addEventListener("click", () => this.save());
      this.input.addEventListener("keydown", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          this.save();
        }
        // Escape puts the row back rather than saving a half-typed name.
        if (event.key === "Escape") this.close();
      });
    }

    open() {
      this.form.hidden = false;
      this.editButton.hidden = true;
      if (this.text) this.text.hidden = true;
      this.input.removeAttribute("aria-invalid");
      this.input.focus();
      this.input.select();
    }

    close() {
      this.form.hidden = true;
      this.editButton.hidden = false;
      if (this.text) this.text.hidden = false;
    }

    /* Liquid's `json` filter serialises a line item's properties as an array of
       [key, value] pairs, not an object - so the naive parse gave an array, and
       setting a named key on an array is dropped by JSON.stringify. The line
       then went back to the cart with no properties at all. Both shapes are
       accepted here in case that ever changes. */
    properties() {
      var raw;
      try {
        raw = JSON.parse(this.getAttribute("data-properties") || "{}");
      } catch (e) {
        return {};
      }
      if (!raw) return {};
      if (!Array.isArray(raw)) return raw;

      var out = {};
      raw.forEach(function (pair) {
        if (pair && pair.length === 2 && pair[0]) out[pair[0]] = pair[1];
      });
      return out;
    }

    async save() {
      var name = this.getAttribute("data-name");
      /* The same shape the product form sends (assets/surname-preview.js):
         upper case, no space at either end, one space between names. The
         print reads the property as stored, and a name corrected here used
         to be the one line in the order in the case it was typed. */
      var value = (this.input.value || "").toUpperCase().trim().replace(/\s+/g, " ");
      var properties = this.properties();

      // Unchanged: put the row back without spending a request or a reload.
      if ((properties[name] || "") === value) {
        this.close();
        return;
      }

      properties[name] = value;
      this.saveButton.setAttribute("disabled", "disabled");

      var root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

      try {
        var response = await fetch(root + "cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: this.getAttribute("data-line-key"),
            quantity: parseInt(this.getAttribute("data-quantity"), 10) || 1,
            properties: properties,
          }),
        });
        if (!response.ok) throw new Error("cart/change rejected");
        this.refresh();
      } catch (e) {
        /* Deliberately NOT close(): putting the row back the way it was is what
           a cancel looks like, so a save that failed read as one that worked
           and the student left with the misspelling they had just corrected.
           The field stays open, holding what they typed, for a second press. */
        this.saveButton.removeAttribute("disabled");
        this.input.setAttribute("aria-invalid", "true");
        this.input.focus();
      }
    }

    /* Changing the properties changes the line key, so the row on screen cannot
       be patched - the cart has to be re-read.

       In the drawer that is a re-render of the drawer, which is what the theme
       does for its own cart changes (cart:refresh, handled in assets/theme.js).
       Reloading there would throw the shopper off whatever page they had the
       drawer open on, which is a heavy price for fixing a spelling. On the cart
       page there is nothing to lose and no partial render to trust, so it
       reloads, exactly as Impact's own quantity control does. */
    refresh() {
      if (this.closest("cart-drawer")) {
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        return;
      }
      window.location.reload();
    }
  }

  if (!customElements.get("line-item-property")) {
    customElements.define("line-item-property", LineItemProperty);
  }
})();
